import React from 'react'
import { PullRefresh, List, Loading } from 'react-vant'
import { useTranslation } from 'react-i18next'
import EmptyState from '@/components/EmptyState'
import type { RefreshListProps } from './index.type'

export default function RefreshList<T = any>({
  data,
  renderItem,
  keyExtractor,
  loading = false,
  hasMore = true,
  onRefresh,
  onLoadMore,
  renderSkeleton,
  skeletonCount = 5,
  emptyText,
  emptyComponent,
  finishedText,
  loadingText,
  className = '',
  itemClassName = '',
  pullRefreshProps,
  listProps,
  disableAutoLoad = false
}: RefreshListProps<T>) {
  const { t } = useTranslation()
  const [isAtTop, setIsAtTop] = React.useState(true)
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [hasEverHadData, setHasEverHadData] = React.useState(false)

  // 使用翻译的默认值
  const defaultEmptyText = emptyText || t('暂无数据')
  const defaultFinishedText = finishedText || t('没有更多数据了')
  const defaultLoadingText = loadingText || t('加载中')

  // 空状态渲染
  const renderEmpty = () => {
    if (emptyComponent) return emptyComponent

    return <EmptyState text={defaultEmptyText} />
  }

  // 骨架屏渲染
  const renderSkeletonList = () => {
    if (!renderSkeleton) return null
    
    return (
      <div className={itemClassName}>
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <React.Fragment key={`skeleton-${index}`}>
            {renderSkeleton()}
          </React.Fragment>
        ))}
      </div>
    )
  }

  // 包装 onLoadMore 以匹配 List 组件的类型要求
  const handleLoad = async () => {
    if (onLoadMore) {
      await onLoadMore()
    }
  }

  // 判断是否有数据
  const hasData = data.length > 0
  
  // 使用 ref 来追踪上一次的数据长度
  const prevDataLengthRef = React.useRef(data.length)
  
  
  // 跟踪是否曾经有过数据，并在数据从有变无时重置
  React.useEffect(() => {
    const prevLength = prevDataLengthRef.current
    const currentLength = data.length
    
    
    // 如果数据从有变成无（被清空），重置 hasEverHadData
    if (prevLength > 0 && currentLength === 0) {
      setHasEverHadData(false)
    }
    // 如果有数据，设置 hasEverHadData 为 true
    else if (hasData) {
      setHasEverHadData(true)
    }
    
    // 更新 ref
    prevDataLengthRef.current = currentLength
  }, [data.length, hasData])

  // 监听滚动位置，只有在顶部时才允许下拉刷新
  React.useEffect(() => {
    const handleScroll = () => {
      // 检查 window 滚动
      const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop
      
      // 检查最近的滚动父元素
      let element = document.querySelector('.overflow-y-auto') as HTMLElement
      const containerScrollTop = element ? element.scrollTop : 0
      
      // 任一滚动容器在顶部就允许下拉刷新
      setIsAtTop(windowScrollTop <= 10 && containerScrollTop <= 10)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    // 监听可能的容器滚动
    const container = document.querySelector('.overflow-y-auto')
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true } as any)
    }
    
    handleScroll() // 初始化时检查一次
    
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (container) {
        container.removeEventListener('scroll', handleScroll as any)
      }
    }
  }, [])

  // 包装 onRefresh，追踪刷新状态
  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await onRefresh()
    } finally {
      setIsRefreshing(false)
    }
  }

  // 首次加载：loading=false 且 data=[] 时，自动触发加载（可通过 disableAutoLoad 禁用）
  React.useEffect(() => {
    if (!disableAutoLoad && !loading && !hasData) {
      onRefresh()
    }
  }, [])

  return (
    <PullRefresh
      className={`h-full ${className}`}
      headHeight={80}
      onRefresh={handleRefresh}
      {...pullRefreshProps}
      disabled={!isAtTop || pullRefreshProps?.disabled}
      pullingText={t('下拉即可刷新')}
      loosingText={t('释放即可刷新')}
      loadingText={t('加载中')}
    >
      {loading && !hasData && !isRefreshing && !hasEverHadData ? (
        // 首次加载中且从未有过数据
        renderSkeleton ? (
          renderSkeletonList()
        ) : (
          <div className="flex items-center justify-center py-20">
            <Loading />
            <span className="ml-2 text-gray-400 text-sm">{defaultLoadingText}</span>
          </div>
        )
      ) : !hasData && !hasEverHadData ? (
        // 无数据且从未有过数据：显示空状态（包括刷新时）
        // 刷新时继续显示空状态，避免切换到空列表导致闪烁
        <>
          {renderEmpty()}
        </>
      ) : (
        // 有数据 或 曾经有过数据：显示列表
        // 刷新时保持列表显示，避免切换
        <List
          finished={!hasMore}
          onLoad={handleLoad}
          finishedText={<div className='text-gray-400 text-sm py-6'>{defaultFinishedText}</div>}
          loadingText={<div className='text-gray-400 text-sm py-6'>{defaultLoadingText}</div>}
          {...listProps}
        >
          <div className={itemClassName}>
            {data.map((item, index) => (
              <React.Fragment key={keyExtractor?.(item, index) || index}>
                {renderItem(item, index)}
              </React.Fragment>
            ))}
          </div>
        </List>
      )}
    </PullRefresh>
  )
}
